import { Alert, Box, Button, CircularProgress, Collapse, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { memo, useCallback, useState } from 'react'
import { IoCaretDown, IoCaretUp } from 'react-icons/io5'

import { useAccount } from '@/hooks'
import { useLazyGetQuestionOptionsQuery } from '@/services/api'
import { Question, QuestionOption } from '@/types'

import { QuestionCardOptionsActions } from '.'

export interface QuestionCardOptionsProps {
  question: Question
  canEdit?: boolean
}

export const QuestionCardOptions = memo<QuestionCardOptionsProps>(
  ({ question, canEdit = false }) => {
    const theme = useTheme()
    const { teacher } = question
    const { user } = useAccount()
    const [expanded, setExpanded] = useState<boolean>(false)
    const [feedbackVisible, setFeedbackVisible] = useState<Record<number, boolean>>({})
    const [options, setOptions] = useState<QuestionOption[]>([])
    const [trigger, { isLoading, error }] = useLazyGetQuestionOptionsQuery()

    const handleToggle = useCallback(async () => {
      if (!expanded && options.length === 0 && !isLoading) {
        const options = await trigger(question.id).unwrap()

        setOptions(options || [])
      }

      setExpanded((prev) => !prev)
    }, [expanded, options.length, isLoading, trigger, question.id])

    const handleFeedback = useCallback((order: number) => {
      setFeedbackVisible((prevState) => ({
        ...prevState,
        [order]: !prevState[order],
      }))
    }, [])

    const getOptionLetter = useCallback(
      (order: string) => String.fromCharCode(96 + parseInt(order)) + ')',
      [],
    )

    const getErrorMessage = () => {
      if (error) {
        if ('status' in error) {
          return `Erro ${error.status}: Falha ao carregar opções`
        }

        return 'Erro ao carregar opções da questão'
      }

      return ''
    }

    const sortedOptions = options.slice().sort((a, b) => a.order - b.order)

    if (question.type !== 'multiple choice') return null

    return (
      <Box data-qa="question-card-options">
        <Button
          onClick={handleToggle}
          variant="text"
          endIcon={expanded ? <IoCaretUp /> : <IoCaretDown />}
          disabled={isLoading}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            p: 0,
            minWidth: 'auto',
          }}>
          {isLoading ? 'Carregando...' : 'Ver opções'}
        </Button>

        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Box mt={2}>
            {isLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 80,
                }}>
                <CircularProgress size={24} />
              </Box>
            ) : error ? (
              <Alert severity="error" variant="outlined" sx={{ mb: 2 }}>
                {getErrorMessage()}
              </Alert>
            ) : sortedOptions.length > 0 ? (
              sortedOptions.map((option) => (
                <Box
                  key={option.order}
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    marginBottom: 2,
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: option.its_correct
                      ? theme.palette.success.light + '20'
                      : theme.palette.error.light + '20',
                    border: `1px solid ${
                      option.its_correct
                        ? theme.palette.success.light
                        : theme.palette.error.light
                    }`,
                  }}>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body1"
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 1,
                        fontWeight: 500,
                        mb: option.feedback ? 1 : 0,
                      }}>
                      <Typography
                        component="span"
                        sx={{ fontWeight: 700, minWidth: '24px' }}>
                        {getOptionLetter(option.order.toString())}
                      </Typography>
                      <Box dangerouslySetInnerHTML={{ __html: option.option }} />
                    </Typography>

                    {option.feedback && (
                      <>
                        <Button
                          variant="text"
                          size="small"
                          onClick={() => handleFeedback(option.order)}
                          sx={{
                            textTransform: 'none',
                            fontSize: '0.75rem',
                            p: 0.5,
                            minWidth: 'auto',
                          }}>
                          {feedbackVisible[option.order] ? 'Ocultar' : 'Ver'} feedback
                        </Button>
                        <Collapse
                          in={feedbackVisible[option.order] || false}
                          timeout="auto"
                          unmountOnExit>
                          <Box
                            sx={{
                              mt: 1,
                              p: 2,
                              borderRadius: 1,
                              backgroundColor: theme.palette.info.light + '20',
                              border: `1px solid ${theme.palette.info.light}`,
                            }}>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              dangerouslySetInnerHTML={{ __html: option.feedback }}
                            />
                          </Box>
                        </Collapse>
                      </>
                    )}
                  </Box>

                  {user?.coduser === teacher.coduser && (
                    <QuestionCardOptionsActions
                      option={option}
                      options={options}
                      canEdit={canEdit}
                    />
                  )}
                </Box>
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhuma opção encontrada
              </Typography>
            )}
          </Box>
        </Collapse>
      </Box>
    )
  },
)

QuestionCardOptions.displayName = 'QuestionCardOptions'
